/**
 * Animated Button Component
 *
 * Feature-rich button with shimmer/glow effects inspired by 21st.dev
 * Built on top of shadcn/ui Button component with design tokens
 */

"use client";

import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface AnimatedButtonProps extends ComponentPropsWithoutRef<typeof Button> {
  /**
   * Animation variant
   * - shimmer: Subtle shine effect that moves across button
   * - glow: Pulsing glow effect around button
   * - pulse: Breathing scale animation
   * - none: No animation
   */
  animation?: "shimmer" | "glow" | "pulse" | "none";

  /**
   * Enable scale effect on hover
   */
  scaleOnHover?: boolean;
}

export const AnimatedButton = forwardRef<HTMLButtonElement, AnimatedButtonProps>(
  ({ className, animation = "shimmer", scaleOnHover = true, children, ...props }, ref) => {
    const animationClasses = {
      shimmer:
        "relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-200%] hover:before:translate-x-[200%] before:transition-transform before:duration-[1200ms] before:ease-out",
      glow: "animate-glow-pulse",
      pulse: "animate-pulse-slow",
      none: "",
    };

    const scaleClass = scaleOnHover ? "hover:scale-105 active:scale-95" : "";

    return (
      <Button
        ref={ref}
        className={cn(
          "transition-all duration-300",
          animationClasses[animation],
          scaleClass,
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

AnimatedButton.displayName = "AnimatedButton";
